import React from 'react'; // { useState }
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { Box, Flex, Grid, Heading, Text } from '@chakra-ui/react';
import { format } from 'date-fns';
import { FETCH_ASSESSMENT } from 'query';

import { BaseButton, ModalBackButton, SpinnerComponent } from '../../../components';

import { Container, View } from './styles';

const ApplicantAssessmentHome = () => {
  const navigate = useNavigate();
  const { assessmentId } = useParams();
  const [deadlinePassed, setDeadlinePassed] = React.useState(false);
  const { data: assessmentData, loading } = useQuery(FETCH_ASSESSMENT, {
    variables: { id: assessmentId },
  });
  const assessment = assessmentData?.fetchAssessmentById?.data || [];

  const duration = assessment.duration ? assessment.duration + ':00' : null;

  const questionCount = assessment?.sections?.flatMap((section) => section.questions).length;
  const sectionCount = assessment?.sections?.flatMap((section) => section).length;

  function handleAssessmentStart() {
    navigate(`/applicant/assessment/${assessmentId}/start`);
  }

  const loader = (
    <Container>
      <Grid minH="50vh" placeContent="center" width="601px">
        <SpinnerComponent data-testid="loading" />
      </Grid>
    </Container>
  );

  if (loading) return loader;

  const deadline = format(new Date(assessment?.deadline), 'do MMMM, yyyy');
  if (assessment?.deadline < new Date() && !deadlinePassed) {
    setDeadlinePassed(true);
  }

  return (
    <Container>
      <View>
        <Grid>
          <ModalBackButton
            onClick={() => {
              window.location.replace(`/applicant/dashboard#assessments`);
            }}
            width={'fit-content'}
          />
        </Grid>
        <div className="mainframe">
          <Flex direction="column" gap="26px">
            <div>
              <Text fontWeight={'800'} color={'var(--black)'} fontSize="32px">
                {assessment?.title}
              </Text>
              <Text color={'var(--grey-500)'} fontSize="16px">
                Find below the details of your assessment, you must complete once started
              </Text>
            </div>
            <div>
              <div className="instructions">
                <Flex gap={'24px'}>
                  <Box width="66px" height="66px">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="66"
                      height="66"
                      viewBox="0 0 66 66"
                      fill="none">
                      <path
                        d="M57.1 46.4999L55 47.6999C54.5 47.9999 54.4 48.5999 54.6 49.0999C54.8 49.3999 55.1 49.5999 55.5 49.5999C55.7 49.5999 55.8 49.5999 56 49.4999L58.2 48.1999C58.7 47.8999 58.8 47.2999 58.6 46.7999C58.2 46.3999 57.6 46.1999 57.1 46.4999Z"
                        fill="#3E7EEE"
                      />
                      <path
                        d="M64 47.6001L60.5999 41.7001C60.2999 41.2001 59.7 41.1001 59.2 41.3001L58.0999 41.9001C57.6999 39.9001 56.5 37.3001 55.7999 36.0001L51.5 28.7001C50.9 27.7001 49.8999 27.1001 48.7999 27.1001V13.5001V13.4001C48.7999 13.3001 48.7999 13.2001 48.7 13.1001C48.6 13.0001 48.5999 12.9001 48.5 12.8001L37.2 1.4001C37 1.2001 36.7 1.1001 36.2999 1.1001H36.2H6.69995C3.89995 1.1001 1.69995 3.3001 1.69995 6.1001V60.1001C1.69995 62.9001 3.89995 65.1001 6.69995 65.1001H43.7999C46.5999 65.1001 48.7999 62.9001 48.7999 60.1001V57.6001L63.5999 49.0001C63.7999 48.9001 64 48.7001 64.0999 48.4001C64.1999 48.1001 64.0999 47.9001 64 47.6001ZM46.7999 60.1001C46.7999 61.7001 45.3999 63.1001 43.7999 63.1001H6.69995C4.99995 63.1001 3.69995 61.7001 3.69995 60.1001V6.1001C3.69995 4.4001 4.99995 3.1001 6.69995 3.1001H35.5V9.8001C35.5 12.4001 37.6 14.5001 40.2 14.5001H46.9V27.9001C46.6 28.1001 46.2999 28.4001 46.0999 28.7001C45.6999 28.4001 45.2999 28.1001 44.7999 28.0001C44 27.8001 43.0999 27.9001 42.2999 28.3001C41.7 28.6001 41.2999 29.1001 41 29.7001C40.7999 29.6001 40.6 29.5001 40.4 29.5001C39.6 29.3001 38.7 29.4001 37.9 29.8001C37.5 30.0001 37.2 30.3001 37 30.6001L34.2999 26.0001C33.4 24.5001 31.4 23.9001 29.9 24.8001C29.1 25.2001 28.6 25.9001 28.4 26.8001C28.1999 27.6001 28.3 28.5001 28.7 29.3001L32.5999 36.0001L31.6 35.4001C30.3 34.9001 28.9 34.9001 27.6 35.6001C26.8 36.1001 26.1 36.8001 25.7 37.7001C25.5 38.2001 25.7 38.7001 26.1 38.9001L32.0999 42.5001C34.0999 43.7001 36.6 46.0001 38.7 48.0001C39.9 49.1001 40.9 50.1001 41.9 50.9001C42 51.0001 42.1 51.0001 42.2 51.1001L41.2 51.7001C41 51.8001 40.7999 52.1001 40.7 52.3001C40.6 52.6001 40.7 52.8001 40.7999 53.1001L44.2 59.0001C44.4 59.3001 44.6999 59.5001 45.0999 59.5001C45.2999 59.5001 45.3999 59.5001 45.5999 59.4001L46.7999 58.7001V60.1001ZM45.5 57.2001L43.0999 53.0001L59.4 43.6001L61.7999 47.8001L45.5 57.2001Z"
                        fill="#3E7EEE"
                      />
                      <path
                        d="M8.6 12.3999V18.7999C8.6 19.3999 9 19.7999 9.6 19.7999C10.2 19.7999 10.6 19.3999 10.6 18.7999V11.3999C10.6 10.7999 10.2 10.3999 9.6 10.3999H7.5C6.9 10.3999 6.5 10.7999 6.5 11.3999C6.5 11.9999 6.9 12.3999 7.5 12.3999H8.6ZM9.6 8.8999C10.2 8.8999 10.6 8.4999 10.6 7.8999V7.3999C10.6 6.7999 10.2 6.3999 9.6 6.3999C9 6.3999 8.6 6.7999 8.6 7.3999V7.8999C8.6 8.3999 9.1 8.8999 9.6 8.8999ZM14.4 10.3999H31.6C32.2 10.3999 32.6 9.9999 32.6 9.3999C32.6 8.7999 32.2 8.3999 31.6 8.3999H14.4C13.8 8.3999 13.4 8.7999 13.4 9.3999C13.4 9.8999 13.9 10.3999 14.4 10.3999ZM14.4 14.8999H33C33.6 14.8999 34 14.4999 34 13.8999C34 13.2999 33.6 12.8999 33 12.8999H14.4C13.8 12.8999 13.4 13.2999 13.4 13.8999C13.4 14.4999 13.9 14.8999 14.4 14.8999ZM14.4 19.3999H43C43.6 19.3999 44 18.9999 44 18.3999C44 17.7999 43.6 17.3999 43 17.3999H14.4C13.8 17.3999 13.4 17.7999 13.4 18.3999C13.4 18.9999 13.9 19.3999 14.4 19.3999ZM26.9 21.8999H14.5C13.9 21.8999 13.5 22.2999 13.5 22.8999C13.5 23.4999 13.9 23.8999 14.5 23.8999H27C27.6 23.8999 28 23.4999 28 22.8999C27.9 22.3999 27.5 21.8999 26.9 21.8999ZM8.5 21.8999C7.9 21.8999 7.5 22.2999 7.5 22.8999C7.5 23.4999 7.9 23.8999 8.5 23.8999H10.5C11.1 23.8999 11.5 23.4999 11.5 22.8999C11.5 22.2999 11.1 21.8999 10.5 21.8999H8.5ZM14.5 28.3999H24.2C24.8 28.3999 25.2 27.9999 25.2 27.3999C25.2 26.7999 24.8 26.3999 24.2 26.3999H14.5C13.9 26.3999 13.5 26.7999 13.5 27.3999C13.5 27.9999 13.9 28.3999 14.5 28.3999ZM10.5 26.3999H8.5C7.9 26.3999 7.5 26.7999 7.5 27.3999C7.5 27.9999 7.9 28.3999 8.5 28.3999H10.5C11.1 28.3999 11.5 27.9999 11.5 27.3999C11.5 26.8999 11 26.3999 10.5 26.3999ZM26.5 30.9999H14.5C13.9 30.9999 13.5 31.3999 13.5 31.9999C13.5 32.5999 13.9 32.9999 14.5 32.9999H26.5C27.1 32.9999 27.5 32.5999 27.5 31.9999C27.5 31.3999 27 30.9999 26.5 30.9999ZM10.5 30.9999H8.5C7.9 30.9999 7.5 31.3999 7.5 31.9999C7.5 32.5999 7.9 32.9999 8.5 32.9999H10.5C11.1 32.9999 11.5 32.5999 11.5 31.9999C11.5 31.3999 11 30.9999 10.5 30.9999ZM14.5 37.4999H22.7C23.3 37.4999 23.7 37.0999 23.7 36.4999C23.7 35.8999 23.3 35.4999 22.7 35.4999H14.5C13.9 35.4999 13.5 35.8999 13.5 36.4999C13.5 36.9999 13.9 37.4999 14.5 37.4999ZM10.5 35.4999H8.5C7.9 35.4999 7.5 35.8999 7.5 36.4999C7.5 37.0999 7.9 37.4999 8.5 37.4999H10.5C11.1 37.4999 11.5 37.0999 11.5 36.4999C11.5 35.8999 11 35.4999 10.5 35.4999ZM14.5 41.9999H22.7C23.3 41.9999 23.7 41.5999 23.7 40.9999C23.7 40.3999 23.3 39.9999 22.7 39.9999H14.5C13.9 39.9999 13.5 40.3999 13.5 40.9999C13.5 41.4999 13.9 41.9999 14.5 41.9999ZM10.5 39.9999H8.5C7.9 39.9999 7.5 40.3999 7.5 40.9999C7.5 41.5999 7.9 41.9999 8.5 41.9999H10.5C11.1 41.9999 11.5 41.5999 11.5 40.9999C11.5 40.3999 11 39.9999 10.5 39.9999ZM13.5 45.4999C13.5 46.0999 13.9 46.4999 14.5 46.4999H29.6C30.2 46.4999 30.6 46.0999 30.6 45.4999C30.6 44.8999 30.2 44.4999 29.6 44.4999H14.5C13.9 44.4999 13.5 44.8999 13.5 45.4999ZM10.5 44.4999H8.5C7.9 44.4999 7.5 44.8999 7.5 45.4999C7.5 46.0999 7.9 46.4999 8.5 46.4999H10.5C11.1 46.4999 11.5 46.0999 11.5 45.4999C11.5 44.8999 11 44.4999 10.5 44.4999ZM37.7 49.4999V58.7999C37.7 59.3999 37.3 59.7999 36.7 59.7999H8.5C7.9 59.7999 7.5 59.3999 7.5 58.7999V49.4999C7.5 48.8999 7.9 48.4999 8.5 48.4999H36.7C37.2 48.4999 37.7 48.8999 37.7 49.4999Z"
                        fill="#3E7EEE"
                      />
                    </svg>
                  </Box>

                  <Flex gap="11px 0" direction="column">
                    <div>
                      <Text color={'#3B74D9'} fontSize={'24px'} fontWeight="800">
                        Assessment Instructions
                      </Text>
                      <Text color={'#82868A'} fontSize={'16px'} lineHeight={'26.5px'}>
                        {`In this assessment, we aim to evaluate your skills and expertise as a ${assessment?.title}.`}
                      </Text>
                    </div>
                    <hr />
                    <Heading color="#23273B" fontWeight="700" fontSize="16px">
                      {`Please allocate a maximum of ${checkTime(
                        assessment?.duration
                      )} to complete this assessment.`}
                    </Heading>{' '}
                    <p style={{ color: '#82868A', fontSize: '16px' }}>
                      {assessment?.sections[0]?.instructions
                        ? assessment?.sections[0]?.instructions
                        : 'No instruction found.'}
                      . <span style={{ color: 'black' }}>Good luck!</span>
                    </p>{' '}
                  </Flex>
                </Flex>
              </div>
              <hr />
              <footer className="footer">
                <InfoFlex label="Sections" value={`${sectionCount} Section`} />
                <InfoFlex label="Questions" value={`${questionCount} Questions`} />
                <InfoFlex label="Duration" value={duration} />
                <InfoFlex label="Deadline" value={deadline} includeHorizontalRule={false} />

                <Flex justifyContent={'flex-end'}>
                  {!deadlinePassed ? (
                    <BaseButton w="140px" onClick={handleAssessmentStart}>
                      <Text fontSize="14px">Start Assessment</Text>
                    </BaseButton>
                  ) : (
                    <Box mt="3rem">Assessment deadline passed.</Box>
                  )}
                </Flex>
              </footer>
            </div>
          </Flex>
        </div>
      </View>
    </Container>
  );
};

export { ApplicantAssessmentHome };

export function checkTime(time) {
  if (time?.startsWith('00:')) {
    const minutes = time?.split(':')[1];
    return `${minutes} minutes`;
  } else {
    return time;
  }
}

export function InfoFlex({ label, value, includeHorizontalRule = true }) {
  return (
    <Flex gap="36px" alignItems="center">
      <div>
        <Text color="#8D9296" fontSize="18px" fontWeight="500">
          {label}
        </Text>
        <Text color="#232638" fontSize="16px" fontWeight="500">
          {value}
        </Text>
      </div>
      {includeHorizontalRule && (
        <div style={{ width: '2px', height: '30px', background: '#D6D9DE' }}></div>
      )}
    </Flex>
  );
}
